interface StatBarChartRow {
  key: string;
  label: string;
  value: number;
  colorClass: string; // Tailwind bg-* class for the bar fill
}

interface Props {
  title: string;
  rows: StatBarChartRow[];
}

/**
 * Small dependency-free horizontal bar chart. No charting library — this
 * app's data volumes are always small (dozens of brands, a capped history
 * list), so a hand-rolled CSS-width bar is simpler and lighter than pulling
 * in a charting dependency for a handful of bars.
 */
export function StatBarChart({ title, rows }: Props) {
  const total = rows.reduce((sum, r) => sum + r.value, 0);

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-card" role="img" aria-label={`${title}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}`}>
      <p className="text-sm font-semibold text-ink">{title}</p>
      <div className="mt-3 space-y-2.5">
        {rows.map((row) => {
          const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
          return (
            <div key={row.key}>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium text-ink">{row.label}</span>
                <span className="text-muted">{row.value}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-stone-100">
                <div className={`h-full rounded-full ${row.colorClass}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
